import React, { useRef, useState, useEffect } from 'react';
import { Tooltip, Grid, Typography, Box, Collapse, Paper, Stack } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { StyledTitle } from './styles';
import { colors } from 'theme/colors';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { HighlightText } from '../HighlightText';

export interface TooltipRow {
  label: string;
  value: string | number;
}
export interface TooltipSize {
  minWidth?: number | string;
  maxWidth?: number | string;
  padding?: number | string;
}

export interface ITooltipInfoCellProps {
  rows: TooltipRow[];
  title: string;
  iconType: EIconTypes;
  tooltipSize?: TooltipSize;
  markedText?: string;
}

interface ITooltipContent {
  rows: ITooltipInfoCellProps['rows'];
  title: string;
  markedText?: string;
}
const TooltipContent = ({ rows, title, markedText }: ITooltipContent) => (
  <Grid container columnSpacing="20px" sx={{ rowGap: 2 }}>
    <Grid size={{ xs: 12 }}>
      <StyledTitle>{title}</StyledTitle>
    </Grid>
    {rows.map((row, index) => (
      <Grid size={{ xs: 12, sm: 6 }} key={index}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {row.label}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {markedText ? (
              <HighlightText text={emptyFieldHolder(row.value)} query={markedText} mark />
            ) : (
              emptyFieldHolder(row.value)
            )}
          </Typography>
        </Stack>
      </Grid>
    ))}
  </Grid>
);

const TooltipInfoCell = ({
  rows,
  title,
  tooltipSize,
  iconType,
  markedText,
}: ITooltipInfoCellProps) => {
  const { minWidth = 300, maxWidth = 982, padding = '20px' } = tooltipSize ?? {};
  const { isDesktop } = useThemeBreakpoint();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isDesktop || !open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDesktop, open]);

  const desktopTooltipStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: ' 0px 8px 16px 0px #04305614, 0px 8px 8px 0px #04305629',
    borderRadius: '8px',
    border: `1px solid ${colors.primary500}`,
    padding: padding as string,
    minWidth: minWidth as number,
    maxWidth: maxWidth as number,
  };

  if (isDesktop) {
    return (
      <Tooltip
        title={<TooltipContent rows={rows} title={title} markedText={markedText} />}
        placement={isDesktop ? 'right-start' : 'bottom'}
        arrow={false}
        slotProps={{
          tooltip: {
            style: desktopTooltipStyle,
          },
        }}
      >
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          <Icon type={iconType} fontSize={20} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box ref={wrapperRef}>
      <Box component="span" onClick={() => setOpen((prev) => !prev)}>
        <Icon type={iconType} fontSize={20} />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          width: 'calc(100vw - 65px)',
          margin: 'auto',
          zIndex: '999',
        }}
      >
        <Collapse in={open} timeout={160} unmountOnExit>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#ffffff',
              boxShadow: ' 0px 8px 16px 0px #04305614, 0px 8px 8px 0px #04305629',
              borderRadius: '8px',
              border: `1px solid ${colors.primary500}`,
              padding,
              width: 'calc(100vw - 65px)',
              boxSizing: 'border-box',
            }}
          >
            <TooltipContent rows={rows} title={title} markedText={markedText} />
          </Paper>
        </Collapse>
      </Box>
    </Box>
  );
};

export default TooltipInfoCell;
